/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "baseui/button";
import { ProjectContext } from "../../UserApp";
import { FileInfoPair, Path, PathType } from "../../Api/DataTransformation";
import { useTransformEngineStyles } from "../TransformNodes/TransformEngineStyles";
import NodeLogo from "../NodeLogo";
import { CreateTransformEngineNodeHandle } from "../TransformNodes/TransformEngineNodeComponent";
import {
  nodeHasEdge,
  TransformEngineNodeData,
  TransformEngineNodeHandle,
} from "../TransformNodes/Util/TransformEngineDataUtils";
import {
  ComposerNodeDescriptor,
  FileType,
  getFileMimeType,
} from "../../Api/Api";
import { BiEdit, BiFile } from "react-icons/bi";
import { FormControl } from "baseui/form-control";
import { Input } from "baseui/input";
import { useHeTree } from "he-tree-react";
import { IoMdDownload } from "react-icons/io";
import { useDexterTheme } from "../../DesignSystem/Theme/ThemeContext";
import { NodeSyntaxHighlighter } from "../Components/NodeSyntaxHighlighter";
import { ChevronDown } from "baseui/icon";
import {
  HeTreeNode,
  buildIsFieldMap,
  buildLockedStructureSnapshot,
  buildComposerTree,
  buildHasChildrenSet,
  convertToHeTree,
  fixInvalidNesting,
  getAllNodeIds,
  hasEmptiedMessage,
  hasNestedService,
  validateLockedStructures,
  deserializeTreeToPaths,
  hasDuplicateFieldNames,
} from "./ComposerNodeHelpers";
import { NodeFileDataModal } from "../../Shared/NodeFileDataModal";
import {
  isFileInfoPair,
  normalizeRawMappingResult,
} from "../TransformNodes/MappingResultUtils";
import { AxiosContext } from "../../Utils/AuthContext";
import { loggedNotify, Component } from "../../Shared/Notify";
import { serializeTreeToContent } from "./ComposerNodeSerializer";
import { PLACEMENT, StatefulTooltip } from "baseui/tooltip";
import { getTooltipOverrides } from "../../DesignSystem/TooltipStyles";
import { ItemSelectorDropdown } from "../Components/ItemSelectorDropdown";

export interface ComposerNodeProps {
  data: TransformEngineNodeData;
}

const SQUARE_SIZE = 100; // px

// Output formats the composer can target (proto | xml). There is deliberately
// no "unknown"/"blank" option; the descriptor defaults to PROTO.
const OUTPUT_FORMATS = [FileType.PROTO, FileType.XML] as const;

// User-facing labels for each format.
const FORMAT_LABELS: Partial<Record<FileType, string>> = {
  [FileType.PROTO]: "Protobuf",
  [FileType.XML]: "XML",
};

// Default selection when the descriptor has no stored format yet.
const DEFAULT_OUTPUT_FORMAT: FileType = FileType.PROTO;

// ─── ComposerNodeComponent ────────────────────────────────────────────────────

export function ComposerNodeComponent({
  data,
}: ComposerNodeProps): JSX.Element {
  const axiosContext = useContext(AxiosContext);
  const { projectState, projectDispatch } = useContext(ProjectContext);

  const {
    FILE_BUTTON_STYLE,
    FILE_ICON_STYLE,
    EDIT_BUTTON_OVERRIDES,
    COMPOSER_SQUARE_NODE_STYLE,
    NODE_HEADER_TEXT_STYLE,
    SELECTED_NODE_BORDER_STYLE,
  } = useTransformEngineStyles();

  const [_, setIsDraggingHandle] = useState(false);

  const { rfNodes } = projectState;
  const node = rfNodes.find((node) => node.id == data.dataID);
  const isSelected = node?.selected ?? false;

  const handle = data.handles?.[0] as TransformEngineNodeHandle;
  const composerDescriptor = data.descriptor as ComposerNodeDescriptor;
  const handleRef = useRef<HTMLDivElement>(null);

  const composerData = useMemo((): Path[][] => {
    return composerDescriptor?.composerData || [];
  }, [composerDescriptor?.composerData]);

  const [isFileModalOpen, setIsFileModalOpen] = useState(false);

  // Target output format, read from the descriptor and updated via the
  // `setOutputFormat` action, which persists with the saved mapping.
  const targetFormat =
    composerDescriptor?.targetFormat ?? DEFAULT_OUTPUT_FORMAT;

  const formatOptions = useMemo(
    () =>
      OUTPUT_FORMATS.map((format) => ({
        label: FORMAT_LABELS[format] ?? format,
        onClick: () =>
          projectDispatch([
            "setOutputFormat",
            { nodeId: data.dataID, targetFormat: format },
          ]),
      })),
    [data.dataID, projectDispatch]
  );

  useEffect(() => {
    if (!isFileModalOpen) return;

    const blockDeleteKeys = (event: KeyboardEvent): void => {
      if (event.key === "Delete" || event.key === "Backspace") {
        event.stopPropagation();
      }
    };

    window.addEventListener("keydown", blockDeleteKeys, true);
    return () => window.removeEventListener("keydown", blockDeleteKeys, true);
  }, [isFileModalOpen]);

  // Gather current mapping result content as base file data for updates
  const composerContent: string | undefined = React.useMemo(() => {
    if (isFileInfoPair(data.mappingResult)) {
      const result = data.mappingResult as FileInfoPair;
      return result.content;
    }
    return undefined;
  }, [data.mappingResult]);

  const [currentTreeData, setCurrentTreeData] = React.useState<
    HeTreeNode[] | null
  >(null);

  // Reset any cached reordering whenever the server output changes (edge added/removed,
  // input node disconnected). The new composerContent from the server is the source of
  // truth; any previous drag state is now stale.
  React.useEffect(() => {
    setCurrentTreeData(null);
  }, [composerContent]);

  // Check if composer has transformation data connected to it. If not, reset composer data
  useEffect(() => {
    const composerNodeDescriptor = data.descriptor as ComposerNodeDescriptor;
    if (!composerNodeDescriptor) return;

    const hasComposerData =
      composerNodeDescriptor.composerData &&
      composerNodeDescriptor.composerData.length > 0;

    const isAlreadyReset =
      composerNodeDescriptor.type === FileType.UNDETECTED &&
      composerNodeDescriptor.sourceInputNode == null &&
      !hasComposerData &&
      composerNodeDescriptor.isTransformReady === false;

    const needsReset =
      !hasComposerData ||
      (hasComposerData && !nodeHasEdge(projectState.rfEdges, data.dataID));

    if (needsReset && !isAlreadyReset) {
      const modifiedNodeData: TransformEngineNodeData = {
        ...data,
        mappingResult: null,
      };

      const modifiedComposerNodeDescriptor: ComposerNodeDescriptor = {
        ...composerNodeDescriptor,
        type: FileType.UNDETECTED,
        sourceInputNode: null,
        composerData: [],
        isTransformReady: false,
      };

      projectDispatch([
        "modifyComposerNodeData",
        { modifiedNodeData, modifiedComposerNodeDescriptor },
      ]);
    }
  }, [projectState.rfEdges, data, projectDispatch]);

  // Compute the initial tree shape from composerData .
  // This ensures that a project loaded with pre-saved composerData is serialized correctly
  // on first render, without requiring any user interaction.
  const initialHeTreeData = React.useMemo(() => {
    if (!composerData || composerData.length === 0) return null;
    const syntaxHighlightedText = (text: string): JSX.Element => {
      const fileType =
        composerDescriptor.sourceInputNode?.type || composerDescriptor.type;
      const code = fileType === FileType.XML ? `<${text}>` : text;
      return <NodeSyntaxHighlighter code={code} fileType={fileType} />;
    };
    const built = buildComposerTree(composerData, syntaxHighlightedText);
    return convertToHeTree(built);
  }, [composerData, composerDescriptor]);

  // Serialize using the drag tree if present, otherwise use the initial tree
  // derived from composerData (covers project-load with pre-saved ordering).
  const reorderedContent: string | undefined = React.useMemo(() => {
    if (!composerContent) return undefined;
    const treeSource = currentTreeData ?? initialHeTreeData;
    if (!treeSource) {
      return composerContent;
    }
    return serializeTreeToContent(
      treeSource,
      composerDescriptor?.type,
      composerContent
    );
  }, [
    currentTreeData,
    initialHeTreeData,
    composerContent,
    composerDescriptor?.type,
  ]);

  // Keep mappingResult.content in sync with the reordered tree output, so
  // anything else in the app reading node.data.mappingResult sees the
  // reordered content — not just this component's local reorderedContent memo.
  React.useEffect(() => {
    if (reorderedContent === undefined) return;
    // No actual reordering vs. what's already stored — skip to avoid a
    // pointless dispatch and to break the composerContent -> reorderedContent
    // -> mappingResult -> composerContent cycle.
    if (reorderedContent === composerContent) return;

    const normalized = normalizeRawMappingResult(data.mappingResult);
    const baseMappingResult = isFileInfoPair(normalized)
      ? (normalized as FileInfoPair)
      : null;
    if (!baseMappingResult) return;

    const modifiedNodeData: TransformEngineNodeData = {
      ...data,
      mappingResult: {
        ...baseMappingResult,
        content: reorderedContent,
      } as FileInfoPair,
    };

    projectDispatch([
      "modifyComposerNodeData",
      {
        modifiedNodeData,
        modifiedComposerNodeDescriptor: composerDescriptor,
      },
    ]);
  }, [
    reorderedContent,
    composerContent,
    data,
    composerDescriptor,
    projectDispatch,
  ]);

  // Called when tree is set to a new valid state.
  // Converts the reordered HeTreeNode[] back to Path[][] and updates composer data
  const handleTreeChange = useCallback(
    (tree: HeTreeNode[]): void => {
      setCurrentTreeData(tree);

      const newComposerData: Path[][] = deserializeTreeToPaths(tree);
      if (newComposerData.length === 0) {
        return;
      }

      projectDispatch([
        "modifyComposerNodeData",
        {
          modifiedNodeData: data,
          modifiedComposerNodeDescriptor: {
            ...composerDescriptor,
            composerData: newComposerData,
          },
        },
      ]);
    },
    [data, composerDescriptor, projectDispatch]
  );

  // TODO: Move this function in here and Output node to a utils file
  // Handle download functionality same as the Output Node
  const handleDownload = useCallback((): void => {
    if (!reorderedContent) {
      loggedNotify.negative(
        axiosContext,
        Component.TRANSFORM_ENGINE,
        "Composer has no content to download"
      );
      return;
    }

    const fileTypeEnum = composerDescriptor?.type;
    const fileExtension = fileTypeEnum
      ? fileTypeEnum.toLowerCase()
      : FileType.UNDETECTED;
    const mimeType = getFileMimeType(fileExtension);

    // Extract original file name from the mapping result
    const fileName = data.descriptor.displayName;

    const downloadName = `${fileName}.${fileExtension}`;

    const blob = new Blob([reorderedContent], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    loggedNotify.positive(
      axiosContext,
      Component.TRANSFORM_ENGINE,
      "File downloaded successfully"
    );
  }, [
    data.descriptor.displayName,
    reorderedContent,
    composerDescriptor?.type,
    axiosContext,
  ]);

  const startTitleEditing = useCallback((nodeId: string) => {
    projectDispatch(["startTitleEditing", { nodeId }]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateTitleEdit = useCallback((nodeId: string, wipTitle: string) => {
    projectDispatch(["updateTitleEdit", { nodeId, wipTitle }]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmTitleEdit = useCallback((nodeId: string) => {
    projectDispatch(["confirmTitleEdit", { nodeId }]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const discardTitleEdit = useCallback((nodeId: string) => {
    projectDispatch(["discardTitleEdit", { nodeId }]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      style={{
        position: "relative",
        minWidth: SQUARE_SIZE,
        minHeight: SQUARE_SIZE,
        margin: "0 auto",
      }}
      tabIndex={0}
    >
      {handle &&
        CreateTransformEngineNodeHandle(
          (handleRefElement) => {
            if (handleRefElement) {
              handleRef.current = handleRefElement;
              handleRefElement.style.position = "absolute";
              handleRefElement.style.left = "-8px";
              handleRefElement.style.top = "50%";
              handleRefElement.style.transform = "translateY(-50%)";
            }
          },
          handle,
          PathType.INPUT,
          0,
          setIsDraggingHandle
        )}

      <div
        style={{
          ...COMPOSER_SQUARE_NODE_STYLE,
          minWidth: 300,
          width: "auto",
          height: "auto",
          minHeight: 100,
          ...(isSelected ? SELECTED_NODE_BORDER_STYLE : {}),
        }}
      >
        {/* ── Header ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            position: "relative",
            minHeight: 34,
          }}
        >
          {/* Data Logo */}
          <div style={{ marginRight: "8px", marginLeft: "5px" }}>
            <NodeLogo
              fileTypeFromDescriptor={
                (data.descriptor as ComposerNodeDescriptor).type
              }
            />
          </div>

          {/* Title and Edit button */}
          <div
            style={{
              flexGrow: 1,
              flexShrink: 1,
              minWidth: 0,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {composerData && composerData.length > 0 ? (
              <>
                {!data.titleEditInProgress ? (
                  <>
                    <span
                      style={{ ...NODE_HEADER_TEXT_STYLE, fontSize: "1.3rem" }}
                    >
                      {data.title.toLocaleLowerCase()}
                    </span>
                    <Button
                      size="mini"
                      kind="tertiary"
                      onClick={() => startTitleEditing(data.dataID)}
                      overrides={EDIT_BUTTON_OVERRIDES}
                    >
                      <BiEdit />
                    </Button>
                  </>
                ) : (
                  <div className="nodrag" style={{ flexGrow: 1 }}>
                    <FormControl>
                      <Input
                        autoFocus
                        value={data.titleInProgress ?? ""}
                        type="text"
                        size="mini"
                        onChange={(e) =>
                          updateTitleEdit(data.dataID, e.target.value)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            data.titleInProgress.trim().length === 0
                              ? discardTitleEdit(data.dataID)
                              : confirmTitleEdit(data.dataID);
                          } else if (e.key === "Escape") {
                            discardTitleEdit(data.dataID);
                          }
                          e.stopPropagation();
                        }}
                        onBlur={() => discardTitleEdit(data.dataID)}
                      />
                    </FormControl>
                  </div>
                )}
              </>
            ) : (
              <span style={{ ...NODE_HEADER_TEXT_STYLE, fontSize: "1.3rem" }}>
                Composer
              </span>
            )}
          </div>
          <Button
            size="mini"
            style={{ ...FILE_BUTTON_STYLE, marginLeft: "30px" }}
            onClick={() => setIsFileModalOpen(true)}
            disabled={!composerContent}
          >
            <BiFile style={FILE_ICON_STYLE} size={19} />
          </Button>

          {/* File preview modal */}
          <NodeFileDataModal
            isOpen={isFileModalOpen}
            onClose={() => setIsFileModalOpen(false)}
            header={`${data.descriptor.displayName} — preview`}
            fileType={composerDescriptor?.type ?? FileType.UNDETECTED}
            composerFileContent={reorderedContent}
          />
        </div>

        {/* Output format selector.
            Stored on the descriptor via setOutputFormat and persisted with
            the mapping. Does not yet drive which transform runs.*/}
        <div
          className="nodrag"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "4px 5px 8px",
          }}
        >
          <ItemSelectorDropdown
            title={FORMAT_LABELS[targetFormat] ?? targetFormat}
            itemSelectActions={formatOptions}
            endEnhancer={<ChevronDown />}
          />
        </div>

        {/* Tree Structure */}
        <ComposerTreeView
          composerData={composerData}
          composerDescriptor={composerDescriptor}
          onTreeChange={handleTreeChange}
          onDownload={handleDownload}
        />
      </div>
    </div>
  );
}

// ─── ComposerTreeView ─────────────────────────────────────────────────────────

function ComposerTreeView({
  composerData,
  composerDescriptor,
  onTreeChange,
  onDownload,
}: {
  composerData: Path[][];
  composerDescriptor: ComposerNodeDescriptor;
  onTreeChange?: (tree: HeTreeNode[]) => void;
  onDownload?: () => void;
}): JSX.Element {
  const { currentTheme } = useDexterTheme();
  const axiosContext = useContext(AxiosContext);

  // ── Syntax highlighting helper ──
  const syntaxHighlightedText = useCallback(
    (text: string): JSX.Element => {
      const fileType =
        composerDescriptor.sourceInputNode?.type || composerDescriptor.type;
      const code = fileType === FileType.XML ? `<${text}>` : text;
      return <NodeSyntaxHighlighter code={code} fileType={fileType} />;
    },
    [composerDescriptor]
  );

  // ── Build tree structure from composerData ──
  const buildTree = React.useMemo(
    () => buildComposerTree(composerData, syntaxHighlightedText),
    [composerData, syntaxHighlightedText]
  );

  // ── Convert to HeTree format ──
  const heTreeData = React.useMemo(
    () => convertToHeTree(buildTree),
    [buildTree]
  );

  // ── State ──
  const [treeData, setTreeData] = React.useState(heTreeData);
  const [expandedNodes, setExpandedNodes] = React.useState<
    Set<string | number>
  >(new Set());

  // previousValidTreeStructureRef always holds the most recent VALID tree state.
  // This is the ground truth used for validation and revert.
  const previousValidTreeStructureRef = React.useRef(treeData);

  // ── Sync tree and expand all when external composerData changes ──
  React.useEffect(() => {
    const allIds = getAllNodeIds(heTreeData);
    setExpandedNodes(new Set(allIds));
    setTreeData(heTreeData);
    previousValidTreeStructureRef.current = heTreeData; // reset last good tree on external data change
  }, [heTreeData]);

  // ── Post-drop validation ──
  // Runs after every treeData change. If the new tree is invalid, reverts to previousValidTreeStructureRef.current.
  // If valid (possibly with fixes), records it as the new previousValidTreeStructureRef.
  React.useEffect(() => {
    const previousValidTreeStructure = previousValidTreeStructureRef.current;

    // Skip if treeData is the same reference as previousValidTreeStructure (e.g. initial mount or revert)
    if (treeData === previousValidTreeStructure) return;

    // ── Run all validation checks ──

    if (hasNestedService(treeData)) {
      loggedNotify.warning(
        axiosContext,
        Component.TRANSFORM_ENGINE,
        "Service fields cannot be nested inside other fields"
      );
      setTreeData(previousValidTreeStructure);
      return;
    }

    // If a message only has a single field prevent from emptying the message entirely
    const hasChildrenSet = buildHasChildrenSet(previousValidTreeStructure);
    if (hasEmptiedMessage(treeData, hasChildrenSet)) {
      loggedNotify.warning(
        axiosContext,
        Component.TRANSFORM_ENGINE,
        "Structures of type 'message' cannot be empty"
      );
      setTreeData(previousValidTreeStructure);
      return;
    }

    const lockedSnapshot = buildLockedStructureSnapshot(
      previousValidTreeStructure
    );

    if (!validateLockedStructures(treeData, lockedSnapshot)) {
      loggedNotify.warning(
        axiosContext,
        Component.TRANSFORM_ENGINE,
        "Structures of type 'oneof', 'enum', or 'service' cannot be modified"
      );
      setTreeData(previousValidTreeStructure);
      return;
    }

    const isFieldMap = buildIsFieldMap(previousValidTreeStructure);
    const { fixed, hoisted } = fixInvalidNesting(
      treeData,
      isFieldMap,
      false,
      0
    );

    if (treeData.length > 0 && fixed.length === 0) {
      loggedNotify.warning(
        axiosContext,
        Component.TRANSFORM_ENGINE,
        "Fields must be a descendant of a 'message' or 'service'"
      );
      setTreeData(previousValidTreeStructure);
      return;
    }

    if (hoisted.length > 0) {
      loggedNotify.warning(
        axiosContext,
        Component.TRANSFORM_ENGINE,
        "Fields must be a descendant of a 'message' or 'service'"
      );
      setTreeData(previousValidTreeStructure);
      return;
    }

    // Duplicate check must run AFTER hoisting, not before — a field dropped
    // inside another field only reveals a name collision with its eventual
    // siblings once fixInvalidNesting has hoisted it out to their level.
    // Checking raw treeData here would miss exactly that case, since the
    // dragged node is still nested one level too deep to be seen as a
    // sibling of the field it collides with.
    if (hasDuplicateFieldNames(fixed)) {
      loggedNotify.warning(
        axiosContext,
        Component.TRANSFORM_ENGINE,
        "A field or structure with that name already exists in the target group"
      );
      setTreeData(previousValidTreeStructure);
      return;
    }

    // Valid — record as last good state
    // If fixInvalidNesting produced a different tree (e.g. hoisting), apply it
    previousValidTreeStructureRef.current = fixed;
    if (fixed !== treeData) {
      setTreeData(fixed);
    }
  }, [axiosContext, treeData]);

  // ── Node expansion toggle ──
  const toggleNodeExpansion = useCallback((nodeId: string | number): void => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  // ── CSS animations (injected once) ──
  React.useEffect(() => {
    const styleId = "he-tree-animations";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        .he-tree * { transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease !important; }
        .he-tree-drag-placeholder {
          background-color: ${
            currentTheme.colors.backgroundTertiary || "rgba(30, 136, 229, 0.1)"
          } !important;
          border: 2px dashed rgba(30, 136, 229, 0.3) !important;
          border-radius: 4px !important;
          min-height: 30px !important;
        }
        .he-tree-dragging {
          opacity: 0.6 !important;
          transform: scale(1.05) rotate(2deg) !important;
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2) !important;
          z-index: 1000 !important;
        }
        .he-tree-drag-over { background-color: ${
          currentTheme.colors.backgroundTertiary || "rgba(30, 136, 229, 0.05)"
        } !important; }
      `;
      document.head.appendChild(style);
    }
    return () => {
      const style = document.getElementById(styleId);
      if (style) style.remove();
    };
  }, [currentTheme]);

  // ── he-tree onChange handler ──
  // onChange just sets state — all validation happens in the useEffect above.
  // useCallback with [] makes the reference stable so useHeTree never re-caches it.
  const userDidDragRef = React.useRef(false);
  const stableOnChange = useCallback(
    (newData: HeTreeNode[]) => {
      userDidDragRef.current = true;
      setTreeData(newData);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Only fire onTreeChange when the tree settles on a valid state that came from
  // an actual user drag
  React.useEffect(() => {
    if (
      treeData === previousValidTreeStructureRef.current &&
      userDidDragRef.current
    ) {
      userDidDragRef.current = false;
      onTreeChange?.(treeData);
    }
  }, [treeData, onTreeChange]);

  // ── he-tree setup ──
  const { renderTree } = useHeTree({
    data: treeData,
    dataType: "tree",
    childrenKey: "children",
    openIds: Array.from(expandedNodes),
    onChange: stableOnChange,
    renderNode: ({ node }: any) => {
      const heNode = node as HeTreeNode;
      const hasChildren = !!(heNode.children && heNode.children.length > 0);
      const isExpanded = expandedNodes.has(heNode.id);

      const handleToggle = (e: React.MouseEvent): void => {
        e.stopPropagation();
        if (hasChildren) toggleNodeExpansion(heNode.id);
      };

      return (
        <div
          className="he-tree-node"
          style={{
            padding: "4px 8px",
            marginBottom: "2px",
            lineHeight: "1.5",
            display: "flex",
            alignItems: "center",
            color: currentTheme.colors.text,
            borderRadius: "4px",
            transition: "background-color 0.2s ease, transform 0.1s ease",
            position: "relative",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor =
              currentTheme.colors.backgroundTertiary ||
              currentTheme.colors.selectedTabColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          {/* Chevron */}
          <div
            onClick={handleToggle}
            style={{
              width: "20px",
              height: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: hasChildren ? "pointer" : "default",
              marginRight: "4px",
              opacity: hasChildren ? 1 : 0,
              transition: "transform 0.2s ease",
              transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
              color: currentTheme.colors.secondary || "#999",
            }}
          >
            {hasChildren && <ChevronDown />}
          </div>
          {/* Drag handle */}
          <div
            style={{
              marginRight: "8px",
              opacity: 0.4,
              fontSize: "14px",
              fontWeight: "bold",
              transition: "opacity 0.2s ease",
              color: currentTheme.colors.secondary || "#999",
              cursor: "grab",
            }}
          >
            ⋮⋮
          </div>
          {/* Label */}
          <div style={{ flexGrow: 1 }}>
            {heNode._original?.label ? heNode._original.label() : heNode.name}
          </div>
        </div>
      );
    },
  });

  // ── Empty state ──
  if (!composerData || composerData.length === 0) {
    return (
      <div
        style={{ padding: "10px", color: currentTheme.colors.defaultTextColor }}
      >
        Set format and connect input data to start building an output
      </div>
    );
  }

  // ── Render tree ──
  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <div className="nodrag" style={{ flexGrow: 1, minWidth: 0 }}>
        {renderTree()}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "12px 0",
          borderTop: `1px solid ${currentTheme.colors.inputNodeBorderColor}`,
          marginTop: "8px",
        }}
      >
        <StatefulTooltip
          content={"Download output"}
          placement={PLACEMENT.bottom}
          overrides={getTooltipOverrides(currentTheme)}
          showArrow
        >
          <Button
            size="compact"
            style={{
              padding: "0 10px",
              minWidth: 32,
              minHeight: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: currentTheme.colors.fileButtonColor,
              backgroundColor: currentTheme.colors.secondary,
            }}
            onClick={onDownload}
          >
            <IoMdDownload size={20} />
          </Button>
        </StatefulTooltip>
      </div>
    </div>
  );
}
